"use client";

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { shopQueryKeys } from "./shop-query-keys";
import {
  getPopularShops,
  getShopsByCategoryId,
  searchShops,
  getAllShops,
  getShopsWithAssociations,
  getShopById,
  getShopByIdWithAssociations,
} from "@/app/admin/shops/_api/shop-reads";
import { ContractStatus, MerchantStatus, ShopStatus } from "@/openapi/generated";
import type {
  ShopsByCategoryIdResponse,
  SearchShopsResponse,
  GetAllShopsResponse,
  ShopsWithAssociationsResponse,
  ShopByIdResponse,
  ShopByIdWithAssociationsResponse,
  PopularShopsResponse,
  ShopExtensionConfigResponse,
} from "@/app/admin/shops/_api/shop-reads";
import { getShopExtensionConfig } from "@/app/admin/shops/_api/shop-server-actions";
import {POPULAR_SHOP_CATEGORY_ID} from "@/lib/constants";


/**
 * Hook to fetch popular shops
 */
export function usePopularShops(
  params: { page?: number; size: number },
  options?: Omit<UseQueryOptions<PopularShopsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.popular(params),
    queryFn: () => getPopularShops(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch shops by category
 */
export function useShopsByCategory(
  categoryId: string,
  params: { page?: number; size: number },
  options?: Omit<UseQueryOptions<ShopsByCategoryIdResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.byCategory({ categoryId, ...params }),
    queryFn: () => getShopsByCategoryId({ categoryId, ...params }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook to search shops
 */
export function useSearchShops(
  term: string,
  params: { page?: number; size: number },
  options?: Omit<UseQueryOptions<SearchShopsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.search({ term, ...params }),
    queryFn: () => searchShops({ term, ...params }),
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    enabled: term.trim().length > 0, // Only search if there's a term
    ...options,
  });
}

// /**
//  * Hook to fetch all shops
//  */
// export function useAllShops(
//   params: { page?: number; size: number },
//   options?: Omit<UseQueryOptions<GetAllShopsResponse>, "queryKey" | "queryFn">
// ) {
//   return useQuery({
//     queryKey: shopQueryKeys.allShops(params),
//     queryFn: () => getAllShops(params),
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     ...options,
//   });
// }

/**
 * Hook to fetch shops with associations (for admin)
 */
export function useShopsWithAssociations(
  params: {
    page?: number;
    size?: number;
    name?: string;
    sort?: string[];
    status?: ShopStatus[];
    categoryId?: string;
    createdAtFrom?: string;
    createdAtTo?: string;
    updatedAtFrom?: string;
    updatedAtTo?: string;
    contractStatuses?: ContractStatus[];
    merchantStatuses?: MerchantStatus[];
    monetized?: boolean;
    uncategorized?: boolean;
  },
  options?: Omit<UseQueryOptions<ShopsWithAssociationsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.withAssociations(params),
    queryFn: () => getShopsWithAssociations(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook to fetch a single shop by ID
 */
export function useShopById(
  id: string,
  options?: Omit<UseQueryOptions<ShopByIdResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.detail(id),
    queryFn: () => getShopById(id),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch a single shop with associations by ID
 */
export function useShopByIdWithAssociations(
  id: string,
  options?: Omit<UseQueryOptions<ShopByIdWithAssociationsResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.detailWithAssociations(id),
    queryFn: () => getShopByIdWithAssociations(id),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!id,
    ...options,
  });
}


/**
 * Hook to fetch extension config for a shop
 */
export function useShopExtensionConfig(
  shopId: string,
  options?: Omit<UseQueryOptions<ShopExtensionConfigResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: shopQueryKeys.extensionConfig(shopId),
    queryFn: () => getShopExtensionConfig(shopId),
    staleTime: 5 * 60 * 1000,
    enabled: !!shopId,
    ...options,
  });
}

/** Sentinel value for "Alle Shops" — distinct from popular and any category ID. */
export const ALL_SHOPS_SENTINEL = "all";

/**
 * Generic hook for dynamic shop fetching based on filters
 * This combines all the different shop fetching patterns into one
 */
export function useShops(
  filters: {
    categoryId?: string | null;
    searchTerm?: string;
    page: number;
    size: number;
    isPopular?: boolean;
  },
  options?: Omit<
    UseQueryOptions<
      ShopsByCategoryIdResponse | SearchShopsResponse | GetAllShopsResponse | PopularShopsResponse
    >,
    "queryKey" | "queryFn"
  >
) {
  const { categoryId, searchTerm, page, size, isPopular } = filters;

  // Determine which query to use based on filters
  const getQueryConfig = () => {
    // 1. Search — scoped to category when a real category UUID is provided.
    // POPULAR_CATEGORY_ID is a real UUID — include it in scoped search.
    // Exclude ALL_SHOPS_SENTINEL ("all") which has no DB equivalent.
    if (searchTerm && searchTerm.trim()) {
      const scopedCategoryId = categoryId && categoryId !== ALL_SHOPS_SENTINEL ? categoryId : undefined;
      const searchParams = {
        term: searchTerm,
        page,
        size,
        ...(scopedCategoryId ? { categoryId: scopedCategoryId } : {}),
      };
      return {
        queryKey: shopQueryKeys.search(searchParams),
        queryFn: () => searchShops(searchParams),
      };
    }

    // 2. All shops sentinel — must be checked before popular fallback
    if (categoryId === ALL_SHOPS_SENTINEL) {
      return {
        queryKey: shopQueryKeys.allShops({ page, size }),
        queryFn: () => getAllShops({ page, size }),
      };
    }

    // 3. Popular shops (explicit flag or POPULAR_CATEGORY_ID)
    if (isPopular || categoryId === POPULAR_SHOP_CATEGORY_ID) {
      return {
        queryKey: shopQueryKeys.popular({ page, size }),
        queryFn: () => getPopularShops({ page, size }),
      };
    }

    // 4. Category filter
    if (categoryId) {
      return {
        queryKey: shopQueryKeys.byCategory({ categoryId, page, size }),
        queryFn: () => getShopsByCategoryId({ categoryId, page, size }),
      };
    }

    // Default: popular shops (no params → popular)
    return {
      queryKey: shopQueryKeys.popular({ page, size }),
      queryFn: () => getPopularShops({ page, size }),
    };
  };

  const queryConfig = getQueryConfig();

  return useQuery({
    ...queryConfig,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}